import type { ShowEvent } from '@djgg/creations';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { isSuperadmin } from '../auth/model.js';
import { useAuth } from '../services/context.js';
import { StripPreview } from './StripPreview.js';
import { CreationFilm } from './CreationFilm.js';
import {
  artifactCategory, collectionArtifacts,
  type ShowArtifact, type ShowArtifactCategory, type ShowChapter,
} from './creationShowModel.js';
import './CreationShow.css';

export interface CreationShowProps {
  onEvent?: (event: ShowEvent) => void;
  identity: string;
  title: string;
  chapter: ShowChapter;
  activity: string;
  artifacts: ShowArtifact[];
  running?: boolean;
  ready?: boolean;
  /** What a game that fell back to a stock game doesn't do yet, in one line. */
  notInYetLine?: string;
  failed?: boolean;
  freshness?: string;
  lastUpdatedAt?: number;
  secondary?: string;
  /** Build internals for superadmins; creators never see this drawer. */
  details?: ReactNode;
  actions?: ReactNode;
  choice?: ReactNode;
  choiceKey?: string;
  /** An adapter may promote an urgent server-authored choice immediately. */
  choiceVisible?: boolean;
  /** Truthful target copy supplied by the server-side selection contract. */
  choiceAffects?: string;
  /** Tells the server the creator skipped the choice, when skipping means more than hiding it. */
  onChoiceSkip?: () => void;
  playUrl?: string;
  onPlay?: () => void;
  mascot?: string;
  /** Stable server-authored seed when a film run has already been opened. */
  filmRunSeed?: string;
  filmEnabled?: boolean;
  /** Stops the running build. Kept in the status dock so it stays in view on a phone. */
  onCancel?: () => void;
  cancelling?: boolean;
}

type Drawer = 'collection' | 'details' | null;

const CATEGORY_COPY: Record<ShowArtifactCategory, string> = {
  identity: 'Identity', world: 'Backgrounds', characters: 'Characters', gameplay: 'Gameplay ideas',
  sound: 'Sound', proof: 'Playable proof', extras: 'Extras',
};

function handleDrawerKeyDown(event: React.KeyboardEvent<HTMLElement>, onClose: () => void) {
  if (event.key === 'Escape') {
    event.preventDefault();
    onClose();
    return;
  }
  if (event.key !== 'Tab') return;
  const focusable = [...event.currentTarget.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )];
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/** One warm, game-first stage for discovery, construction and iteration. */
export function CreationShow(props: CreationShowProps) {
  return <ShowSession key={props.identity} {...props} />;
}

function ShowSession(p: CreationShowProps) {
  const { user } = useAuth();
  const details = isSuperadmin(user) ? p.details : undefined;
  const reported = useRef(new Set<ShowEvent>());
  const collectionTrigger = useRef<HTMLButtonElement>(null);
  const detailsTrigger = useRef<HTMLButtonElement>(null);
  const drawerClose = useRef<HTMLButtonElement>(null);
  const [broken, setBroken] = useState<Set<string>>(() => new Set());
  const [reduced, setReduced] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  const [visible, setVisible] = useState(!document.hidden);
  const [online, setOnline] = useState(navigator.onLine);
  const [activity, setActivity] = useState<'match' | null>(null);
  const [invited, setInvited] = useState(false);
  const [invitationDismissed, setInvitationDismissed] = useState(false);
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [viewerId, setViewerId] = useState<string | null>(null);
  const [filmComplete, setFilmComplete] = useState(false);
  const [now, setNow] = useState(Date.now);

  function report(event: ShowEvent) {
    if (reported.current.has(event)) return;
    reported.current.add(event);
    p.onEvent?.(event);
  }

  useEffect(() => { const timer = window.setTimeout(() => report('opened'), 1_000); return () => clearTimeout(timer); }, []);
  useEffect(() => { if (p.ready) report('ready'); }, [p.ready]);
  useEffect(() => {
    if (!p.running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(timer);
  }, [p.running]);
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const motion = () => setReduced(query?.matches ?? false);
    const visibility = () => setVisible(!document.hidden);
    const connection = () => setOnline(navigator.onLine);
    query?.addEventListener?.('change', motion);
    document.addEventListener('visibilitychange', visibility);
    window.addEventListener('online', connection);
    window.addEventListener('offline', connection);
    return () => {
      query?.removeEventListener?.('change', motion);
      document.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('online', connection);
      window.removeEventListener('offline', connection);
    };
  }, []);
  const freshness = p.freshness ?? (p.running && p.lastUpdatedAt && now - p.lastUpdatedAt > 120_000
    ? `Last update ${Math.floor((now - p.lastUpdatedAt) / 60_000)}m ago` : '');
  const collection = collectionArtifacts(p.artifacts).filter(item => !broken.has(item.id));
  const viewer = collection.find(item => item.id === viewerId) ?? null;
  const viewerArtifacts = viewer
    ? collection.filter(item => viewer.group ? item.group === viewer.group : item.id === viewer.id)
    : [];
  const blocked = !visible || !!activity || !!drawer || !!viewer;
  const choiceActive = Boolean(p.choice && !invitationDismissed && (p.choiceVisible || invited));
  useEffect(() => {
    if (!p.running || blocked || p.failed || invited || invitationDismissed) return;
    const timer = window.setTimeout(() => {
      setInvited(true);
      if (p.choice) report('choice-offered');
    }, 30_000);
    return () => clearTimeout(timer);
  }, [p.running, p.failed, blocked, invited, invitationDismissed, p.choiceKey, Boolean(p.choice)]);
  useEffect(() => {
    setInvited(false);
    setInvitationDismissed(false);
  }, [p.choiceKey]);
  useEffect(() => {
    if (p.ready || p.failed) setActivity(null);
  }, [p.ready, p.failed]);
  useEffect(() => { if (activity) report('activity-started'); }, [activity]);
  useEffect(() => { if (drawer) window.setTimeout(() => drawerClose.current?.focus(), 0); }, [drawer]);
  function openDrawer(kind: Exclude<Drawer, null>) {
    setDrawer(kind);
  }
  function closeDrawer() {
    const trigger = drawer === 'collection' ? collectionTrigger.current : detailsTrigger.current;
    setDrawer(null);
    trigger?.focus();
  }
  function openViewer(id: string) {
    setViewerId(id);
    setDrawer(null);
  }

  const status = !online ? 'Connection lost — reconnecting to saved progress'
    : p.failed ? 'This version needs attention'
      : p.ready ? 'Ready to play'
        : p.activity;
  const readyArtifact = collection.find(item => item.kind === 'capture')
    ?? collection.find(item => artifactCategory(item) === 'identity')
    ?? collection.find(item => item.kind !== 'audio');
  const readyVisible = !!p.ready && (filmComplete || p.filmEnabled === false);

  return <section className={`creation-show${p.ready ? ' creation-show--ready' : ''}${p.failed ? ' creation-show--failed' : ''}`}
    aria-label="Game creation show" data-testid="creation-show">
    <header className="creation-show__head">
      <div><span>Deej’s workshop</span><strong>{p.title || 'Your next game'}</strong></div>
      <span className="creation-show__state">{p.ready ? 'Ready' : p.failed ? 'Needs attention' : p.chapter}</span>
    </header>

    <div className="creation-show__stage" aria-hidden={!!drawer}>
      {!filmComplete && p.filmEnabled !== false && <div hidden={!!viewer || choiceActive || !!activity} className="creation-show__film-wrap">
        <CreationFilm identity={p.identity} title={p.title} activity={p.activity} artifacts={p.artifacts}
          active={!blocked && !choiceActive} failed={p.failed} ready={p.ready} notInYetLine={p.notInYetLine}
          failureMessage={p.secondary}
          runSeed={p.filmRunSeed} onFinaleComplete={() => setFilmComplete(true)} />
      </div>}
      {p.filmEnabled === false && !viewer && !choiceActive && !activity && !readyVisible &&
        <StaticShowFallback title={p.title} activity={p.activity} artifact={readyArtifact} failed={p.failed} secondary={p.secondary} reduced={reduced}
          onBroken={() => readyArtifact && setBroken(old => new Set(old).add(readyArtifact.id))} broken={readyArtifact ? broken.has(readyArtifact.id) : false} />}
      {viewer ? <ArtifactViewer artifacts={viewerArtifacts} broken={broken} reduced={reduced}
          onBroken={id => setBroken(old => new Set(old).add(id))}
          onCollection={() => { setViewerId(null); setDrawer('collection'); }} onClose={() => setViewerId(null)} />
        : choiceActive ? <ChoiceStage ready={!!p.ready} affects={p.choiceAffects}
          onSkip={() => { report('choice-skipped'); setInvited(false); setInvitationDismissed(true); p.onChoiceSkip?.(); }}>
          {p.choice}
        </ChoiceStage>
          : activity && !p.ready && !p.failed ? <ShowActivity kind={activity} artifacts={collection} reduced={reduced}
            onClose={() => { setActivity(null); setInvited(false); setInvitationDismissed(true); }} />
            : readyVisible ? <ReadyStage title={p.title} artifact={readyArtifact} broken={readyArtifact ? broken.has(readyArtifact.id) : false}
              reduced={reduced} playUrl={p.playUrl} onPlay={p.onPlay} notInYetLine={p.notInYetLine}
              onBroken={() => readyArtifact && setBroken(old => new Set(old).add(readyArtifact.id))} />
              : null}
    </div>

    {p.ready && p.filmEnabled !== false && (!filmComplete || viewer || choiceActive || drawer) && p.playUrl && <div className="creation-show__ready-banner" role="status">
      <span>Your game is ready whenever you are.</span><a href={p.playUrl} onClick={p.onPlay}>Play now</a>
    </div>}

    {!p.ready && p.playUrl && <div className="creation-show__current-banner">
      <span>{p.failed ? 'Your previous version is safe and still playable.' : 'Your previous version stays playable while this update is made.'}</span>
      <a href={p.playUrl} onClick={p.onPlay}>Play current version</a>
    </div>}

    <div className="creation-show__dock" aria-hidden={!!drawer}>
      <div className="creation-show__activity" role="status" aria-live="polite" aria-atomic="true">
        <span className="creation-show__pulse" aria-hidden="true" /><div><strong>{status}</strong>
          {freshness && <small>{freshness}</small>}</div>
      </div>
      <div className="creation-show__dock-actions">
        {p.onCancel && <button type="button" className="creation-show__cancel-action" disabled={p.cancelling}
          onClick={p.onCancel} data-testid="creation-show-cancel">{p.cancelling ? 'Cancelling…' : 'Cancel build'}</button>}
        <button type="button" className="creation-show__collection-action" ref={collectionTrigger}
          aria-expanded={drawer === 'collection'} onClick={() => openDrawer('collection')}>Collection <span>{collection.length}</span>
          <span className="creation-show__legacy-label">Explore · {collection.length} game assets</span>
        </button>
        {details && <button type="button" className="creation-show__quiet-action" ref={detailsTrigger}
          aria-expanded={drawer === 'details'} onClick={() => openDrawer('details')}>Details<span className="creation-show__legacy-label">Game and build details</span></button>}
      </div>
    </div>

    {!p.ready && !p.failed && !p.choice && !activity && invited && collection.length > 0 && <div className="creation-show__invitation">
      <div><span className="creation-show__eyebrow">While your game keeps building</span><h3>Explore what is already here</h3></div>
      <div><button type="button" onClick={() => openDrawer('collection')}>Open collection</button>
        {collection.filter(item => item.kind === 'image').length >= 2 && <button type="button" onClick={() => setActivity('match')}>Match your game art</button>}
        <button type="button" onClick={() => { setInvited(false); setInvitationDismissed(true); }}>Not now</button></div>
    </div>}

    {p.actions && <div className="creation-show__notice">{p.actions}</div>}
    {drawer && <DrawerPanel kind={drawer} artifacts={collection} ready={!!p.ready} playUrl={p.playUrl}
      onPlay={p.onPlay} closeRef={drawerClose} onClose={closeDrawer} onOpenArtifact={openViewer}
      onBroken={id => setBroken(old => new Set(old).add(id))} onKeyDown={event => handleDrawerKeyDown(event, closeDrawer)}>
      {drawer === 'details' ? details : null}
    </DrawerPanel>}
  </section>;
}

function ArtifactMedia({ artifact, broken, reduced, onBroken }: {
  artifact: ShowArtifact; broken: boolean; reduced: boolean; onBroken: () => void;
}) {
  const category = artifactCategory(artifact);
  if (artifact.kind === 'audio' && artifact.url && !broken) return <div className="creation-show__audio">
    <span aria-hidden="true">♫</span><audio controls preload="none" src={artifact.url} aria-label={`Listen to ${artifact.title}`} />
  </div>;
  if (artifact.kind === 'video' && artifact.url && !broken) return <video className={`creation-show__media creation-show__media--${category}`}
    src={artifact.url} poster={artifact.posterUrl} controls preload="metadata" playsInline aria-label={`${artifact.title} sealed gameplay replay`} onError={onBroken} />;
  if (artifact.strip && artifact.url && !broken) return <div className="creation-show__strip"><StripPreview url={artifact.url}
    strip={artifact.strip} variant="cinematic" playing={!reduced} /></div>;
  if (artifact.url && !broken) return <img className={`creation-show__media creation-show__media--${category}`}
    src={artifact.url} alt={artifact.title} decoding="async" referrerPolicy="no-referrer" onError={onBroken} />;
  return <div className="creation-show__placeholder" role="img" aria-label={`${artifact.title} preview unavailable`}>
    <span aria-hidden="true">{artifact.kind === 'discovery' ? '✦' : artifact.kind === 'audio' ? '♫' : '◇'}</span>
  </div>;
}

function StaticShowFallback({ title, activity, artifact, failed, secondary, reduced, broken, onBroken }: {
  title: string; activity: string; artifact?: ShowArtifact; failed?: boolean; secondary?: string;
  reduced: boolean; broken: boolean; onBroken: () => void;
}) {
  return <div className="creation-show__intro"><div><span className="creation-show__eyebrow">{failed ? 'Build intermission' : 'Making your game'}</span>
    <h2>{failed ? 'Your saved work is safe' : activity}</h2><p>{secondary || `${title} is taking shape. New finished material appears in Collection.`}</p></div>
    {artifact && <ArtifactMedia artifact={artifact} broken={broken} reduced={reduced} onBroken={onBroken} />}</div>;
}

function ReadyStage({ title, artifact, broken, reduced, playUrl, onPlay, notInYetLine, onBroken }: {
  title: string; artifact?: ShowArtifact; broken: boolean; reduced: boolean; playUrl?: string; onPlay?: () => void; notInYetLine?: string; onBroken: () => void;
}) {
  return <div className="creation-show__ready">
    {artifact && <div className="creation-show__ready-art"><ArtifactMedia artifact={artifact} broken={broken} reduced={reduced} onBroken={onBroken} /></div>}
    <div className="creation-show__ready-copy"><span className="creation-show__eyebrow">Ready to play</span><h2>{title || 'Your game is ready'}</h2>
      <p>Your playable game is ready. The complete making-of collection stays here when you want it.</p>
      {notInYetLine && <p data-testid="creation-show-not-in-yet">{notInYetLine}</p>}
      {playUrl ? <a className="creation-show__primary" href={playUrl} onClick={onPlay}>Play your game</a>
        : <p className="creation-show__waiting-link">The play link is being prepared.</p>}</div>
  </div>;
}

function ChoiceStage({ children, ready, affects, onSkip }: {
  children: ReactNode; ready: boolean; affects?: string; onSkip: () => void;
}) {
  return <div className="creation-show__choice"><span className="creation-show__eyebrow">An optional creative choice</span>
    {affects && <p className="creation-show__choice-target">Affects {affects}</p>}{children}
    <div className="creation-show__choice-actions"><button type="button" onClick={onSkip}>{ready ? 'Finish choosing later' : 'Keep making my game'}</button></div></div>;
}

function ArtifactViewer({ artifacts, broken, reduced, onBroken, onCollection, onClose }: {
  artifacts: ShowArtifact[]; broken: ReadonlySet<string>; reduced: boolean; onBroken: (id: string) => void; onCollection: () => void; onClose: () => void;
}) {
  const lead = artifacts[0]!;
  return <div className={`creation-show__viewer${artifacts.length > 1 ? ' creation-show__viewer--compare' : ''}`}><div className="creation-show__viewer-head"><button type="button" onClick={onCollection}>← Collection</button>
    <button type="button" onClick={onClose}>Back to latest</button></div>
    <div className="creation-show__viewer-media">{artifacts.map(artifact => <figure key={artifact.id}>
      <ArtifactMedia artifact={artifact} broken={broken.has(artifact.id) || !!artifact.unavailable} reduced={reduced} onBroken={() => onBroken(artifact.id)} />
      {artifacts.length > 1 && <figcaption>{artifact.title}<span>{artifact.label ?? artifact.state ?? 'Alternative'}</span></figcaption>}
    </figure>)}</div>
    <div><span className="creation-show__eyebrow">{lead.label ?? CATEGORY_COPY[artifactCategory(lead)]}</span><h2>{lead.title}</h2>
      {artifacts.length > 1 && <p>{artifacts.length} alternatives in this set.</p>}
      {lead.summary && <p>{lead.summary}</p>}{lead.version && <p>Version {lead.version}</p>}</div></div>;
}

function DrawerPanel({ kind, artifacts, ready, playUrl, onPlay, closeRef, onClose, onOpenArtifact, onBroken, onKeyDown, children }: {
  kind: Exclude<Drawer, null>; artifacts: ShowArtifact[]; ready: boolean; playUrl?: string; onPlay?: () => void;
  closeRef: React.RefObject<HTMLButtonElement | null>; onClose: () => void; onOpenArtifact: (id: string) => void; onBroken: (id: string) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void; children?: ReactNode;
}) {
  const categories = [...new Set(artifacts.map(artifactCategory))];
  return <aside className="creation-show__drawer" role="dialog" aria-modal="true"
    aria-label={kind === 'collection' ? 'Your collection' : 'Game and build details'} onKeyDown={onKeyDown}>
    <header><div><span className="creation-show__eyebrow">{kind === 'collection' ? 'Everything made for your game' : 'On demand'}</span>
      <h2>{kind === 'collection' ? 'Your collection' : 'Game and build details'}</h2></div>
      <button type="button" ref={closeRef} onClick={onClose} aria-label={`Close ${kind}`}>Close</button></header>
    {ready && playUrl && <div className="creation-show__drawer-ready"><span>Your game is ready.</span><a href={playUrl} onClick={onPlay}>Play now</a></div>}
    {kind === 'collection' ? <div className="creation-show__collection">{categories.length ? categories.map(category => <section key={category}>
      <h3>{CATEGORY_COPY[category]} <span>{artifacts.filter(item => artifactCategory(item) === category).length}</span></h3>
      <div className="creation-show__gallery">{artifacts.filter(item => artifactCategory(item) === category).map(item => <button type="button" key={item.id}
        aria-label={`${item.title} ${item.label ?? item.state ?? item.origin ?? item.kind}`}
        onClick={() => onOpenArtifact(item.id)}>
        <CollectionThumb item={item} onBroken={() => onBroken(item.id)} />
        <strong>{item.title}</strong><span>{item.label ?? item.state ?? item.origin ?? item.kind}</span>
      </button>)}</div>
    </section>) : <p>No game art yet. It appears here as each piece is finished.</p>}</div> : <div className="creation-show__details">{children}</div>}
  </aside>;
}

function CollectionThumb({ item, onBroken }: { item: ShowArtifact; onBroken: () => void }) {
  if (item.strip && item.url) return <span className="creation-show__thumb creation-show__strip"><StripPreview url={item.url} strip={item.strip} variant="cinematic" playing={false} /></span>;
  const still = item.posterUrl ?? (item.kind === 'audio' || item.kind === 'video' ? undefined : item.url);
  if (still) return <span className="creation-show__thumb"><img src={still} alt="" loading="lazy" referrerPolicy="no-referrer" onError={onBroken} /></span>;
  // Sound and an unposed replay have no picture: a slim glyph, not an empty frame.
  return <span className="creation-show__thumb creation-show__thumb--glyph"><i aria-hidden="true">{item.kind === 'audio' ? '♫' : '▶'}</i></span>;
}

function ShowActivity({ artifacts, onClose }: { kind: 'match'; artifacts: ShowArtifact[]; reduced: boolean; onClose: () => void }) {
  const choices = artifacts.filter(item => item.url && item.kind === 'image').slice(0, 4);
  const [round, setRound] = useState(0);
  const [message, setMessage] = useState('');
  return <div className="creation-show__game"><span className="creation-show__eyebrow">Just for fun · your build continues</span>
    <h3>{`Find ${choices[round % Math.max(1, choices.length)]?.title ?? 'an artifact'}`}</h3>
    <div className="creation-show__game-grid">{choices.map(item => <button type="button" key={item.id} aria-label={`Pick ${item.title}`} onClick={() => {
      const correct = item.id === choices[round % choices.length]?.id;
      setMessage(correct ? 'That’s the one!' : 'Try another.');
      if (correct) setRound(value => value + 1);
    }}><img src={item.url} alt={item.title} /></button>)}</div>
    <p aria-live="polite">{message}</p><button type="button" onClick={onClose}>Back to the show</button></div>;
}
